import React from 'react';
import LinkPreviewBadge from './LinkPreviewBadge';
import { Lightbulb, BookOpen, Target } from 'lucide-react';

/**
 * About Me section with life learnings, opinions, and resources.
 * Content organized into 3 themed sections with LinkPreview badges for external links.
 */
const AboutMe = () => {
    const learnings = [
        "Friction is needed for growth.",
        "A great teacher can change your life. AI might finally make personalized education real, tailoring content to every single student's needs.",
        "Get to know yourself. It's the best competitive advantage you can build.",
        { text: "Going to Silicon Valley so early in my career helped me get a wider perspective on the vast amount of options we can choose from nowadays.", link: "https://medium.com/@jonortega20/reflections-on-my-silicon-valley-experience-5c9d9540f1b6", linkText: "Going to Silicon Valley" },
        "I find AI forces me to be more explicit in my communication, and as someone who sometimes assumes things, I've found it's helped me a lot.",
        "The short-term future is made for hyper-specialists. The long-term future is made for generalists and intellectuals.",
        "Focus, discipline, and consistency will take you further than any life-changing opportunity (and keep you sane along the way).",
        { text: "Marginal improvements are not ambitious at all, that's just indefinite optimism. Go for zero-to-one leaps.", link: "https://chengweihu.com/io/indefinite-optimism/", linkText: "indefinite optimism" },
        "In AI, memory compounds intelligence. Progress accelerates when learning accumulates rather than resets. The same applies to humans.",
        "You'll rarely learn from someone else's mistakes. You have to fall into the trap yourself.",
    ];

    const opinions = [
        "My biggest strength is probably agency. I can't stand not doing anything and if I see something doesn't work as I'd expect it to, it's natural to me to step up and build a solution. Pair that with curiosity and it compounds.",
        "I'm used to figuring things out on my own (I built my startup's product solo). But I love working with ambitious people, they get the most out of me.",
        "When giving feedback, try to focus on the big picture, not the details.",
        "I tend to over-communicate at work, always straight to the point though.",
        "I'm not into extreme opinions, there are always trade-offs and I don't find it difficult to change my thoughts if there's a reason for that. As a curious personal story, my friends gave me a hoodie for my birthday saying \"Neither white nor black, grey\", and I think that defines me a lot.",
        { text: "I love thinking about second-order effects and their practical applications.", link: "https://www.graphapp.ai/blog/understanding-2nd-order-effects-impacts-and-implications", linkText: "second-order effects" },
        "We're never ambitious enough, we fear change by default. When dealing with young people, it's the right thing to push them toward the harder path.",
        "It's hard for me to work on something I don't believe in.",
        { text: "Work is increasingly async. That's why I don't really like daily standups. A great supervisor told me to write a Slack message every morning with all the news, and that feels way more appropriate. Instead, I'm a fan of longer weekly meetings as a way to plan the week and align. Also, I find it very useful to communicate through tools like Loom.", link: "https://www.theverge.com/24134914/discord-ceo-gaming-chat-teens-safety-moderation-decoder-interview", linkText: "communicate through tools like Loom" },
        "I avoid Calendly at all costs. It makes you look always available, and just having meetings doesn't make you productive. I prefer to feel the friction of having to schedule, and I've found this helps me say no to unnecessary ones.",
    ];

    const resources = [
        { title: "Stop AI from Killing Critical Thinking", link: "https://www.youtube.com/watch?v=3lPnN8omdPA", explanation: "A TED talk on the future UIs for education." },
        { title: "Agents Lack Creativity", link: "https://www.andrewp.co/blog/agents-lack-creativity", explanation: "An essay segmenting problems into two: those with clear paths and those with no playbook. AI masters the former but not (yet) the latter." },
        { title: "How to Be More Agentic", link: "https://usefulfictions.substack.com/p/how-to-be-more-agentic", explanation: "An essay with tips for developing agency." },
        { title: "Lo que nadie cuenta de las personas superdotadas", link: "https://www.youtube.com/watch?v=6Vm9ql-9o2I", explanation: "A podcast in Spanish on how high IQ people think differently." },
        { title: "Choice Overload", link: "https://thedecisionlab.com/biases/choice-overload-bias", title2: "Commitment Bias", link2: "https://thedecisionlab.com/biases/commitment-bias", explanation: "Two articles on biases. The commitment bias makes you great for other people (you're loyal and predictable) but caps your own growth pace." },
    ];

    // Helper to render learning items (some have inline links with preview)
    const renderLearningItem = (item, idx) => {
        if (typeof item === 'string') {
            return (
                <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                    <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                </li>
            );
        }
        // Item with inline link using LinkPreviewBadge
        const parts = item.text.split(item.linkText);
        return (
            <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                <span>
                    {parts[0]}
                    <LinkPreviewBadge
                        link={item.link}
                        display={item.linkText}
                    />
                    {parts[1]}
                </span>
            </li>
        );
    };

    const renderOpinionItem = (item, idx) => {
        if (typeof item === 'string') {
            return (
                <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                </li>
            );
        }
        // Item with inline link using LinkPreviewBadge
        const parts = item.text.split(item.linkText);
        return (
            <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                <span className="w-1.5 h-1.5 bg-violet-400 rounded-full mt-2 flex-shrink-0" />
                <span>
                    {parts[0]}
                    <LinkPreviewBadge
                        link={item.link}
                        display={item.linkText}
                    />
                    {parts[1]}
                </span>
            </li>
        );
    };

    return (
        <section id="mindset" className="pt-8 pb-20 px-8 bg-white">
            <div className="max-w-5xl mx-auto">
                {/* Section Header */}
                <div className="text-center mb-14">
                    <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-4">
                        About Me
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        A glimpse into how I think, what I've learned, and some topics I'm curious about.
                    </p>
                </div>

                {/* Cards - Stacked Vertically */}
                <div className="space-y-6">
                    {/* Card 1: Core Learnings */}
                    <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                                <Lightbulb className="w-5 h-5 text-amber-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-slate-800">Key learnings</h3>
                        </div>
                        <ul className="space-y-3">
                            {learnings.map((learning, idx) => renderLearningItem(learning, idx))}
                        </ul>
                    </div>

                    {/* Card 2: Opinions & Concepts */}
                    <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                                <Target className="w-5 h-5 text-violet-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-slate-800">Concepts & strong opinions</h3>
                        </div>
                        <ul className="space-y-3">
                            {opinions.map((opinion, idx) => renderOpinionItem(opinion, idx))}
                        </ul>
                    </div>

                    {/* Card 3: Great Resources */}
                    <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                                <BookOpen className="w-5 h-5 text-emerald-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-slate-800">Great resources worth your time</h3>
                        </div>
                        <ul className="space-y-3">
                            {resources.map((resource, idx) => (
                                <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mt-2 flex-shrink-0" />
                                    <span>
                                        <LinkPreviewBadge
                                            link={resource.link}
                                            display={resource.title}
                                        />
                                        {resource.title2 && (
                                            <>
                                                {" & "}
                                                <LinkPreviewBadge
                                                    link={resource.link2}
                                                    display={resource.title2}
                                                />
                                            </>
                                        )}
                                        {resource.explanation && ` → ${resource.explanation}`}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutMe;
